'use client';

/**
 * DetailPage hero header — Phase A2.
 *
 * Anatomy:
 *   ← Back row     [presence stack] [copy-link]
 *   eyebrow / breadcrumb
 *   TITLE                                    [actions]
 *   [status pills]                           [secondary actions]
 *   [optional urgency banner — e.g. SIRS countdown]
 *
 * The hero takes lots of slots but renders them all under one
 * predictable wrapper so every detail page in the platform has the
 * same visual rhythm.
 */

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { PageTitle } from '../typography';

export interface DetailPageHeaderProps {
  /**
   * Optional back-link href. Omit to hide the back row. The header
   * renders a plain anchor by default; pass `backLinkRender` to use
   * a framework router (e.g. Next.js `<Link>`) — see
   * `apps/web/src/components/dashboard-shell-v2.tsx` for an example.
   */
  backHref?: string;
  /** Back-link label (e.g. "Clients", "Incidents"). */
  backLabel?: string;
  /**
   * Render-prop for the back link. Defaults to a plain anchor so the
   * UI package stays framework-agnostic. Web app should wire to a
   * Next.js `<Link>` to keep prefetch + soft-navigation behaviour.
   */
  backLinkRender?: (props: {
    href: string;
    className: string;
    children: React.ReactNode;
  }) => React.ReactNode;
  /** Additional serialisable classes for the back link. */
  backLinkClassName?: string;
  /** Eyebrow text above the title (e.g. "Incident number ABC123", "Care plan"). */
  eyebrow?: React.ReactNode;
  /** Page title — should be the single most-important identifier. */
  title: string;
  /** Status pills row below the title (chips, badges). */
  statusPills?: React.ReactNode;
  /** Primary + secondary action buttons (right side of the title row). */
  actions?: React.ReactNode;
  /** Optional inline banner (e.g. SIRS countdown, deadline warning). */
  banner?: React.ReactNode;
  /** Right-rail slot for presence / collaboration indicators. */
  presence?: React.ReactNode;
  /** Right-rail slot for copy-link / share controls. */
  share?: React.ReactNode;
  /** Override the default header border + background. */
  className?: string;
}

export function DetailPageHeader({
  backHref,
  backLabel,
  backLinkRender,
  backLinkClassName,
  eyebrow,
  title,
  statusPills,
  actions,
  banner,
  presence,
  share,
  className,
}: DetailPageHeaderProps) {
  const backLinkClass = cn(
    'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
    backLinkClassName,
  );
  const backLinkChildren = (
    <>
      <ArrowLeft className="h-3 w-3" aria-hidden="true" />
      {backLabel ?? 'Back'}
    </>
  );
  return (
    <header
      className={cn(
        'flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm',
        className,
      )}
      data-detail-page-header
    >
      {/* Top row: back link + presence/share */}
      {(backHref || presence || share) && (
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {backHref ? (
            backLinkRender ? (
              backLinkRender({
                href: backHref,
                className: backLinkClass,
                children: backLinkChildren,
              })
            ) : (
              <a href={backHref} className={backLinkClass}>
                {backLinkChildren}
              </a>
            )
          ) : (
            <span />
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            {presence}
            {share}
          </div>
        </div>
      )}

      {/* Title + actions row */}
      <div className="flex min-w-0 flex-col items-start gap-3 sm:flex-row sm:justify-between">
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <p className="break-words font-mono text-[11px] uppercase tracking-wider text-slate-400 [overflow-wrap:anywhere]">
              {eyebrow}
            </p>
          )}
          <PageTitle className="mt-1 break-words font-heading text-xl font-bold leading-tight text-statura-ink [overflow-wrap:anywhere]">
            {title}
          </PageTitle>
          {statusPills && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">{statusPills}</div>
          )}
        </div>
        {actions && (
          <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 sm:w-auto sm:max-w-[44rem] sm:justify-end [&>*]:max-w-full [&>*]:min-w-0">
            {actions}
          </div>
        )}
      </div>

      {/* Optional inline banner (urgency / warning / deadline) */}
      {banner}
    </header>
  );
}
